/* eslint-disable max-len */
import React from 'react';
import {
  Alert, AlertTitle, Box,
  Button, CardActions, CardContent, CardHeader, Dialog, Divider, Radio, Snackbar,
} from '@mui/material';
import { get, capitalize } from 'lodash';
import { useMutation } from '@apollo/client';
import { useIdentityContext } from 'react-netlify-identity-gotrue';
import { PetParent } from '../../types/PetParent';
import { STOP_SUPPORT_UNSUBSCRIBE } from '../../queries';

type DialogProps = { open: boolean; setOpen: (state: boolean) => void; petParent: PetParent; };

function StopSupportDialog({
  open,
  setOpen,
  petParent,
}: DialogProps) {
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [checkedOption, setCheckedOption] = React.useState('stop_support');
  const [stopSupportUnsubscribe, { loading }] = useMutation(STOP_SUPPORT_UNSUBSCRIBE, {
    onCompleted: () => {
      setOpen(false);
    },
    onError: () => {
      setOpen(false);
      setSnackbarOpen(true);
      setSnackbarMessage('Error stopping support for pet parent');
    },
    refetchQueries: [
      'GetPetParent',
      'GetTypeformResponses',
    ],
  });

  const firstName = React.useMemo(() => capitalize(petParent.firstName), [petParent.firstName]);
  const lastName = React.useMemo(() => capitalize(petParent.lastName), [petParent.lastName]);

  const identity = useIdentityContext();
  const isAdmin = get(identity, 'user.app_metadata.roles[0]') === 'admin';
  const subheaders = [];
  const alerts = [];

  let animalString = petParent.activeConversation ? `${petParent.activeConversation.animal.name} (${petParent.activeConversation.animal.careType})` : '';
  const fallbackAnimal = petParent.animals?.[0];
  animalString = animalString === '' && fallbackAnimal
    ? `${fallbackAnimal.name} (${fallbackAnimal.careType})`
    : animalString;
  const animalName = React.useMemo(() => capitalize(animalString), [animalString]);

  subheaders.push(`${firstName} ${lastName} is currently receiving support for ${animalName}.`);
  if (petParent.activeConversation) {
    alerts.push((
      <Alert
        sx={{ cursor: 'pointer' }}
        onClick={() => setCheckedOption('stop_support')}
        action={(
          <Radio
            checked={checkedOption === 'stop_support'}
            onChange={() => setCheckedOption('stop_support')}
            value="stop_support"
            name="support-radio"
            inputProps={{ 'aria-label': 'Stop radio button' }}
          />
        )}
        severity="info"
      >
        <AlertTitle>Turn off check-ins</AlertTitle>
        <Box>
          <p>{`Select this option if ${firstName} asks us to stop checking in or reports that they are no longer caring for ${animalName}. This will turn off email and SMS check-ins related to ${animalName}. The pet parent can still reach out for help until their membership expires.`}</p>
        </Box>
      </Alert>
    ));
  }
  if (isAdmin) {
    alerts.push((
      <Alert
        sx={{ cursor: 'pointer' }}
        onClick={() => setCheckedOption('unsubscribe')}
        action={(
          <Radio
            checked={checkedOption === 'unsubscribe'}
            onChange={() => setCheckedOption('unsubscribe')}
            value="unsubscribe"
            name="support-radio"
            inputProps={{ 'aria-label': 'Unsubscribe radio button' }}
          />
      )}
        severity="warning"
      >
        <AlertTitle>Cancel membership & unsubscribe</AlertTitle>
        <Box>
          <p>{`Select this option if ${firstName} asked to cancel their membership or told us not to contact them again. Selecting this option will cancel the pet parent's membership, unsubscribe them from SMS and email, and stop future messages from being sent. This cannot be undone.`}</p>
        </Box>
      </Alert>
    ));
  }
  React.useEffect(() => {
    if (isAdmin && !petParent.activeConversation) {
      setCheckedOption('unsubscribe');
    }
  }, [isAdmin, petParent]);
  return (
    <>
      <Dialog
        open={open}
        scroll="paper"
        onClose={() => setOpen(false)}
        aria-labelledby="stop-support-dialog"
        aria-describedby="stop-support-dialog-description"
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '584px',
            maxWidth: '90vw',
          },
        }}
      >
        <CardHeader
          title="Modify support"
          sx={{ borderBottom: 1, borderColor: 'rgba(0, 0, 0, 0.12)' }}
          subheader={subheaders.join('\n')}
        />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {alerts}
          </Box>
        </CardContent>
        <Divider />
        <CardActions>
          <Button
            variant="contained"
            disabled={!checkedOption || loading}
            onClick={() => stopSupportUnsubscribe({
              variables: {
                email: petParent.email,
                actionerEmail: get(identity, 'user.email'),
                action: checkedOption,
              },
            })}
          >
            Save
          </Button>
          <Button variant="text" onClick={() => setOpen(false)}>Cancel</Button>
        </CardActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
        }}
        open={snackbarOpen}
        message={snackbarMessage}
        key={snackbarMessage}
      />
    </>
  );
}

export default StopSupportDialog;
